import axios from "axios";
import React, { useState, useEffect } from "react";
import { BASE_URL } from "../../../env";
import Sidebar from "../../../components/layout/Sidebar";
import { Row, Col, Form } from "react-bootstrap";
import {
  generateLoanSummaryForMember,
  getReportWindow,
  getFirstAndLastEmiDates,
  formatAmount,
  resolveEffectiveStartDate,
} from "../../../utils/utils";
import Select from "react-select";
import CustomPagination from "../../../components/ui/CustomPagination";
import DataTable from "../../../components/ui/DataTable";
import { useAdminAuth } from "../../auth/AdminAuthProvider";
import DashboardHeader from "../../../components/layout/DashboardHeader";
import { MdExpandMore } from "react-icons/md";
import StandardizedDateInput from "../../../components/ui/StandardizedDateInput";

function GplInterestReport() {
  const { adminUser } = useAdminAuth();
  const [reportData, setReportData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [openSidebarToggle, setOpenSidebarToggle] = useState(window.innerWidth > 1000);

  const [selectedBranchFilter, setSelectedBranchFilter] = useState(null);
  const [selectedEmployeeFilter, setSelectedEmployeeFilter] = useState(null);
  const [fromDateDisbursed, setFromDateDisbursed] = useState("");
  const [toDateDisbursed, setToDateDisbursed] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const fromDate = "2000-01-01"; // Start from early enough to catch all historical data
  // The date the interest is reported "as at", plus the whole-day window used to
  // filter rows. Master uses the same helper, so the two reports cannot drift
  // apart on either the balance date or the day boundary (bugfix.md Bug 20).
  const { start: windowStart, end: windowEnd, asAtDate } = getReportWindow(
    fromDateDisbursed,
    toDateDisbursed
  );
  const itemsPerPage = 20;

  const OpenSidebar = () => {
    setOpenSidebarToggle(!openSidebarToggle);
  };

  useEffect(() => {
    const getReportData = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/api/getGploutstandingreport`,
          { params: { id: adminUser.user.id } }
        );
        setReportData(response.data || []);
      } catch (error) {
        console.error("Error fetching interest report:", error);
      }
    };

    if (adminUser?.user?.id) {
      getReportData();
    }
  }, [adminUser]);

  // Flatten and process report data.
  // Uses the same calculation as the Master report. The previous one,
  // generateOutstandingAmountsForAllMember, ignored the as-at date and always
  // returned today's interest, and returned null for a closed loan so the row
  // was dropped by the filter below. Master is the agreed-correct figure and
  // already counts closed loans (bugfix.md Bug 20, which answers DOUBT 2).
  const flattenedData = reportData
    .map((data) => generateLoanSummaryForMember(data, fromDate, asAtDate))
    .filter((item) => item !== null);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginate = (page) => setCurrentPage(page);

  // Unique options for dropdowns
  const branchOptions = [
    ...new Set(flattenedData.map((d) => d?.branchName)),
  ]
    .filter(Boolean)
    .map((name) => ({ value: name, label: name }));

  const employeeOptions = [
    ...new Set(flattenedData.map((d) => d?.username)),
  ]
    .filter(Boolean)
    .map((name) => ({ value: name, label: name }));

  // Filter Logic
  const filterData = (data) => {
    let filtered = [...data];

    if (fromDateDisbursed && toDateDisbursed) {
      // Whole-day window. This used to compare against a bare
      // `new Date(toDateDisbursed)`, i.e. midnight, which dropped every loan
      // disbursed later that same day (bugfix.md Bug 18 / Bug 20).
      filtered = filtered.filter((item) => {
        const disbursedDate = new Date(item.branchManagerStatusUpdatedAt);
        return disbursedDate >= windowStart && disbursedDate <= windowEnd;
      });
    }

    if (selectedBranchFilter) {
      filtered = filtered.filter(
        (item) => item.branchName === selectedBranchFilter.value
      );
    }

    if (selectedEmployeeFilter) {
      filtered = filtered.filter(
        (item) => item.username === selectedEmployeeFilter.value
      );
    }

    if (searchTerm && searchTerm.trim() !== "") {
      const term = searchTerm.trim().toLowerCase();
      filtered = filtered.filter((item) => {
        const memberName = String(item?.memberName || "").toLowerCase();
        const customerId = String(item?.customerId || "").toLowerCase();
        const loanId = String(item?.loanId || "").toLowerCase();
        const phoneNumber = String(item?.phoneNumber || "").toLowerCase();
        const aadharNo = String(item?.aadharNo || "").toLowerCase();
        const applicationId = String(item?.ApplicationId || "").toLowerCase();

        return (
          memberName.includes(term) ||
          customerId.includes(term) ||
          loanId.includes(term) ||
          phoneNumber.includes(term) ||
          aadharNo.includes(term) ||
          applicationId.includes(term)
        );
      });
    }

    return filtered;
  };

  const totalPages = Math.ceil(filterData(flattenedData).length / itemsPerPage);

  const filteredData = filterData(flattenedData).slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  const columns = [
    { label: "S No", render: (data, index) => indexOfFirstItem + index + 1 },
    { label: "Region", render: (data) => data.regionName },
    { label: "Division", render: (data) => data.divisionName },
    { label: "Branch Name", render: (data) => data.branchName ?? "" },
    { label: "Branch Code", render: (data) => data.branchCode ?? "" },
    { label: "Customer Name", render: (data) => data.memberName ?? "" },
    { label: "Customer ID", render: (data) => data.customerId ?? "" },
    {
      label: "Address",
      render: (data) => {
        const parts = [
          data.permanentDoorNo,
          data.permanentStreetNo,
          data.permanentPanchayat,
          data.permanentTaluk,
          data.permanentDistrict,
          data.permanentPincode
        ].filter(part => part && String(part).trim() !== '');
        return parts.length > 0 ? parts.join(", ") : "N/A";
      },
    },
    { label: "Contact No", render: (data) => data.phoneNumber ?? "" },
    { label: "Loan Type", render: (data) => data.loanType ?? "" },
    {
      label: "Sub Category",
      render: (data) => data?.businessDetails?.natureOfBusiness ?? "",
    },
    { label: "Loan Cycle", render: (data) => data.loanCycle ?? "" },
    { label: "Loan Account No", render: (data) => data.loanId ?? "" },
    {
      label: "Rate of Interest",
      render: (data) => data?.proposedLoanDetails?.rateOfInterest ?? "",
    },
    {
      label: "Disbursement Date",
      render: (data) =>
        data.branchManagerStatus === "disbursed" &&
          data.branchManagerStatusUpdatedAt
          ? new Date(data.branchManagerStatusUpdatedAt).toLocaleDateString(
            "en-GB",
            {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            }
          )
          : "",
    },
    {
      label: "Maturity Date",
      render: (data) => {
        const { lastEmiDate } = getFirstAndLastEmiDates(
          data.branchManagerStatusUpdatedAt,
          data?.proposedLoanDetails?.tenureInMonths,
          data.emiDateByBranchManager,
          resolveEffectiveStartDate(data)
        );
        return lastEmiDate?.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }) ?? "";
      },
    },
    {
      label: "Loan Amount",
      render: (data) =>
        data.branchManagerStatus === "disbursed"
          ? formatAmount(data?.eligibleLoanAmountByBm)
          : "",
    },
    { label: "Employee Name", render: (data) => data.employeeName ?? "" },
    { label: "Prospect ID", render: (data) => data.ApplicationId ?? "" },
    {
      label: "Total Outstanding Interest",
      render: (data) => formatAmount(data.totalOutstandingInterest),
    },
    {
      label: "Total Interest Paid",
      render: (data) => formatAmount(data.interestPaid),
    },
  ];

  return (
    <section className="master-layout">
      <div className="master-sidebar-wrapper">
        <Sidebar
          openSidebarToggle={openSidebarToggle}
          OpenSidebar={OpenSidebar}
        />
      </div>

      <div className="master-content-wrapper">
        <div className="master-header-wrapper">
          <DashboardHeader OpenSidebar={OpenSidebar} />
        </div>

        <div className="master-card-container">
          <div className="master-search-section">
            <h1 className="TableHeaderText w-100 text-start">Interest Report</h1>
            <div className="w-100">
              <Row className="w-100" style={{ fontWeight: "500" }}>
                <Col xs={12} lg={4} md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>Search Customer</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Search Name, Customer ID, Loan Acc No, Phone..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={6} lg={2} md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>From Date</Form.Label>
                    <StandardizedDateInput
                      value={fromDateDisbursed}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFromDateDisbursed(val);
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={6} lg={2} md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>To Date</Form.Label>
                    <StandardizedDateInput
                      value={toDateDisbursed}
                      onChange={(e) => {
                        const val = e.target.value;
                        setToDateDisbursed(val);
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            <button
              className={`filter-toggle-btn ${showMobileFilters ? "active" : ""}`}
              onClick={() => setShowMobileFilters(!showMobileFilters)}
            >
              <span style={{ fontWeight: 600, fontSize: "14px" }}>
                {showMobileFilters ? "Hide Filters" : "Show Filters"}
              </span>
              <MdExpandMore size={22} />
            </button>

            <div className={`w-100 ${!showMobileFilters ? "mobile-hidden" : "mobile-visible"}`}>
              <Row className="w-100" style={{ fontWeight: "500" }}>

                <Col xs={12} lg={4} md={6} className="mb-2">
                  <Form.Label>Branch</Form.Label>
                  <Select
                    value={selectedBranchFilter}
                    onChange={(val) => {
                      setSelectedBranchFilter(val);
                      if (fromDateDisbursed && toDateDisbursed && val && selectedEmployeeFilter) {
                        setTimeout(() => setShowMobileFilters(false), 300);
                      }
                    }}
                    options={branchOptions}
                    isClearable
                    placeholder="Filter by Branch"
                    className="filter-dropdown"
                  />
                </Col>
                <Col xs={12} lg={4} md={6} className="mb-2">
                  <Form.Label>Employee</Form.Label>
                  <Select
                    value={selectedEmployeeFilter}
                    onChange={(val) => {
                      setSelectedEmployeeFilter(val);
                      if (fromDateDisbursed && toDateDisbursed && selectedBranchFilter && val) {
                        setTimeout(() => setShowMobileFilters(false), 300);
                      }
                    }}
                    options={employeeOptions}
                    isClearable
                    placeholder="Filter by Employee"
                    className="filter-dropdown"
                  />
                </Col>
              </Row>
            </div>
          </div>

          <div className="master-list-section">
            <div className="master-table-wrapper" style={{ padding: "0 15px" }}>
              <DataTable
                columns={columns}
                data={filteredData}
                reportName="InterestReport"
                excelData={filterData(flattenedData)}
              />
            </div>

            <div className="master-pagination-wrapper">
              <CustomPagination
                totalPages={totalPages}
                currentPage={currentPage}
                paginate={paginate}
                totalItems={filteredData.length}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default GplInterestReport;
